import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search } from "lucide-react";
import {
  WHATSAPP_BUSINESS_PROFILE_VERTICALS,
  type WhatsAppBusinessProfileVertical,
} from "@multi-chat/shared";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover/index.js";
import { cn } from "../../../lib/utils.js";

export interface AddPhoneCategoryFieldProps {
  value: WhatsAppBusinessProfileVertical | "";
  onChange: (value: WhatsAppBusinessProfileVertical) => void;
  disabled?: boolean;
}

export function AddPhoneCategoryField({
  value,
  onChange,
  disabled = false,
}: AddPhoneCategoryFieldProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const options = useMemo(() => {
    const query = search.trim().toLowerCase();
    return WHATSAPP_BUSINESS_PROFILE_VERTICALS.filter((vertical) => {
      const label = t(`whatsapp.addPhone.categories.${vertical}`).toLowerCase();
      return !query || label.includes(query) || vertical.toLowerCase().includes(query);
    });
  }, [search, t]);
  const selectedLabel = value ? t(`whatsapp.addPhone.categories.${value}`) : null;

  return (
    <label className="grid gap-1 text-start">
      <span className="text-[0.8rem] font-medium text-ink-muted">
        {t("whatsapp.addPhone.fields.category")}
      </span>
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (disabled) return;
          setOpen(next);
          if (!next) setSearch("");
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-line bg-surface px-3 text-start text-sm disabled:opacity-70"
          >
            <span className={cn("min-w-0 truncate", selectedLabel ? "text-ink" : "text-ink-muted")}>
              {selectedLabel ?? t("whatsapp.addPhone.fields.categoryPlaceholder")}
            </span>
            <ChevronDown size={14} className="shrink-0 opacity-60" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(24rem,calc(100vw-2rem))] overflow-hidden p-0">
          <div className="flex items-center gap-2 border-b border-line px-2.5 py-2 focus-within:bg-surface-muted">
            <Search size={14} className="shrink-0 text-ink-muted" aria-hidden="true" />
            <input
              className="min-w-0 flex-1 border-0 bg-transparent text-sm shadow-none ring-0 outline-none focus:border-0 focus:shadow-none focus:ring-0 focus:outline-none focus-visible:border-0 focus-visible:shadow-none focus-visible:ring-0 focus-visible:outline-none"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("whatsapp.addPhone.fields.categorySearch")}
            />
          </div>
          <div
            className="max-h-[17.5rem] overflow-y-auto overscroll-contain p-1"
            onWheel={(event) => event.stopPropagation()}
          >
            {options.map((vertical) => (
              <button
                key={vertical}
                type="button"
                className={cn(
                  "flex w-full rounded-md px-2 py-1.5 text-start text-sm hover:bg-surface-muted",
                  vertical === value ? "bg-surface-muted" : null,
                )}
                onClick={() => {
                  onChange(vertical);
                  setOpen(false);
                  setSearch("");
                }}
              >
                {t(`whatsapp.addPhone.categories.${vertical}`)}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </label>
  );
}
